// A kick-off as a form types it, a day and a wall clock in Italy, and the instant the API speaks. Both apps
// write kick-offs, and the zone is pinned rather than the browser's: a runner on UTC would shift them.

/** Where every match is played, whatever the reader's browser says. */
export const KICKOFF_ZONE = 'Europe/Rome'

/** A `HH:MM` wall clock; a browser may append seconds when the input has a step. */
const TIME = /^(\d{2}):(\d{2})(?::\d{2})?$/

const offsetFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: KICKOFF_ZONE,
  timeZoneName: 'longOffset',
})

const partsFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: KICKOFF_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  // Not `hour12: false`, which answers 24 for midnight in some ICU builds.
  hourCycle: 'h23',
})

/** Italy's UTC offset at a given instant, as `+02:00`. */
function offsetAt(instant: Date): string {
  const name = offsetFormat.formatToParts(instant).find((part) => part.type === 'timeZoneName')
  const offset = name?.value.replace('GMT', '') ?? ''
  return offset === '' ? '+00:00' : offset
}

/** The ISO instant of an Italian wall clock. Two passes, and pinned rather than taken from the
 *  browser: apps/admin-panel/docs/calendario-di-un-girone.md says what each of those costs to get
 *  wrong. */
export function toInstant(date: string, time: string): string | undefined {
  const clock = TIME.exec(time)
  if (date === '' || clock === null) return undefined
  const wall = `${date}T${clock[1]}:${clock[2]}:00`
  const firstGuess = offsetAt(new Date(`${wall}Z`))
  return `${wall}${offsetAt(new Date(`${wall}${firstGuess}`))}`
}

/** The Italian wall clock of an instant: what the operator typed, read back. */
export function fromInstant(iso: string): { readonly date: string; readonly time: string } {
  const parts = new Map(
    partsFormat.formatToParts(new Date(iso)).map((part) => [part.type, part.value]),
  )
  const date = `${parts.get('year') ?? ''}-${parts.get('month') ?? ''}-${parts.get('day') ?? ''}`
  return { date, time: `${parts.get('hour') ?? ''}:${parts.get('minute') ?? ''}` }
}
